import { I18n } from '@calendly/i18n';
import { Table, Header, Body, HeaderCell, Row, Cell, EmptyState } from '@calendly/table';
import { FlexLayout } from '@calendly/ui/components/flex-layout';
import { Icon } from '@calendly/ui/components/icon';
import { AlertWarningTriangleIcon, CheckCircleIcon } from '@calendly/ui/components/icons';
import { Loader } from '@calendly/ui/components/loader';
import { Pagination } from '@calendly/ui/components/pagination';
import { TitleText } from '@calendly/ui/components/typography';
import { VisuallyHidden } from '@calendly/ui/components/visually-hidden';
import { colorIconDestructive, colorIconSuccess } from '@calendly/ui/theme';
import classNames from 'classnames';
import moment from 'moment-timezone';
import { useEffect, useState, forwardRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Divider } from 'features/organization_settings/data_deletion/styles';
import InProgressIcon from 'images/icons/in-progress.svg';
import { dateNotationSelector } from 'selectors/current_user';
import { userDateFormat } from 'services/time_format';
import { ActionsDropdown } from './actions_dropdown';
import { CancelationPopup } from './cancelation_popup';
import { DATA_REQUEST_STATES } from './constants';
import { deletionRequestsSelector, orderBySelector, sortSelector, pageSelector } from './selectors';
import { loadDeletionRequests, setOrderBy, setSort, setPage } from './slice';
import { ViewDetailsPopup } from './view_details_popup';
const containerCss = "ctk1ar6";
const Container = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(containerCss, props.className)} />;
});
const styledIconCss = "sxrz8ji";
const StyledIcon = forwardRef((props, ref) => <Icon ref={ref} {...props} className={classNames(styledIconCss, props.className)} />);
const iconContainerCss = "i1258y48";
const IconContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'span';
  return <Component ref={ref} {...restProps} className={classNames(iconContainerCss, props.className)} />;
});
const greyIconCss = "g14fqoa7";
const GreyIcon = forwardRef((props, ref) => <StyledIcon ref={ref} {...props} className={classNames(greyIconCss, props.className)} />);
const statusContainerCss = "sur18za";
const StatusContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(statusContainerCss, props.className)} />;
});
const warningIconCss = "w1f3jvg";
const WarningIcon = forwardRef((props, ref) => <StyledIcon ref={ref} {...props} className={classNames(warningIconCss, props.className)} />);
const InProgress = () => <IconContainer>
    <InProgressIcon />
  </IconContainer>;
const StatusIcon = ({
  state
}) => {
  switch (state) {
    case DATA_REQUEST_STATES.PENDING:
      return <GreyIcon name="clock" />;
    case DATA_REQUEST_STATES.RUNNING:
      return <InProgress />;
    case DATA_REQUEST_STATES.FINISHED:
      return <CheckCircleIcon spacingRight={4} color={colorIconSuccess} size="large" />;
    case DATA_REQUEST_STATES.ERROR:
      return <AlertWarningTriangleIcon spacingRight={4} color={colorIconDestructive} size="large" />;
    case DATA_REQUEST_STATES.CANCELED:
      return <WarningIcon name="close-circle" />;
    default:
      return null;
  }
};

// i18n-tasks-use I18n.t('publisher.organization_settings.data_deletion.section.deletion_requests.status_column.error')
// i18n-tasks-use I18n.t('publisher.organization_settings.data_deletion.section.deletion_requests.status_column.finished')
// i18n-tasks-use I18n.t('publisher.organization_settings.data_deletion.section.deletion_requests.status_column.pending')
// i18n-tasks-use I18n.t('publisher.organization_settings.data_deletion.section.deletion_requests.status_column.running')
// i18n-tasks-use I18n.t('publisher.organization_settings.data_deletion.section.deletion_requests.status_column.canceled')
const Status = ({
  state
}) => {
  const text = I18n.t(`publisher.organization_settings.data_deletion.section.deletion_requests.status_column.${state}`);
  return <>
      <StatusIcon state={state} />
      <StatusContainer>{text}</StatusContainer>
    </>;
};
const RequestRow = ({
  request,
  onShow,
  onCancel
}) => {
  const dateNotation = useSelector(dateNotationSelector);
  return <Row>
      <Cell label={I18n.t('publisher.organization_settings.data_deletion.section.deletion_requests.created_at')}>
        {userDateFormat(moment(request.created_at), dateNotation, 'date_small_with_year')}
      </Cell>
      <Cell label={I18n.t('publisher.organization_settings.data_deletion.section.deletion_requests.requested_by')}>
        {request.requested_by}
      </Cell>
      <Cell label={I18n.t('publisher.organization_settings.data_deletion.section.deletion_requests.status')}>
        <Status state={request.state} />
      </Cell>
      <Cell hasActions>
        <ActionsDropdown request={request} onShow={onShow} onCancel={onCancel} />
      </Cell>
    </Row>;
};
export const DeletionRequestsSection = () => {
  const dispatch = useDispatch();
  const orderBy = useSelector(orderBySelector);
  const sort = useSelector(sortSelector);
  const page = useSelector(pageSelector);
  const [viewDetailsRequest, setViewDetailsRequest] = useState();
  const [cancelationRequest, setCancelationRequest] = useState();
  const {
    isLoading,
    deletionRequests: {
      pagination,
      collection
    }
  } = useSelector(deletionRequestsSelector);
  const sortedColumn = {
    name: orderBy,
    direction: sort
  };
  useEffect(() => {
    dispatch(loadDeletionRequests());
  }, [dispatch, orderBy, sort, page]);
  const toggleSort = field => {
    if (orderBy !== field || orderBy === field && sort === 'desc') {
      dispatch(setSort('asc'));
    } else {
      dispatch(setSort('desc'));
    }
    dispatch(setOrderBy(field));
  };
  if (!isLoading && collection.length === 0) return null;
  return <>
      <Divider />
      <FlexLayout direction="column" gap={24}>
        <TitleText level={3} size="x-small">
          {I18n.t('publisher.organization_settings.data_deletion.section.deletion_requests.title')}
        </TitleText>
        <FlexLayout direction="column" gap={16}>
          <div data-component="data-deletion-records">
            <Table sortedColumn={sortedColumn} onSort={toggleSort}>
              <Header>
                <HeaderCell columnName="created_at">
                  {I18n.t('publisher.organization_settings.data_deletion.section.deletion_requests.created_at')}
                </HeaderCell>
                <HeaderCell columnName="requested_by">
                  {I18n.t('publisher.organization_settings.data_deletion.section.deletion_requests.requested_by')}
                </HeaderCell>
                <HeaderCell columnName="state">
                  {I18n.t('publisher.organization_settings.data_deletion.section.deletion_requests.status')}
                </HeaderCell>
                <HeaderCell hugContent>
                  <VisuallyHidden>
                    {I18n.t('publisher.organization_settings.data_deletion.section.deletion_requests.details_header')}
                  </VisuallyHidden>
                </HeaderCell>
              </Header>
              <Body>
                {isLoading ? <EmptyState><Loader data-testid="loader" /></EmptyState> : collection.map(request => <RequestRow key={request.id} request={request} onShow={() => setViewDetailsRequest(request)} onCancel={() => setCancelationRequest(request)} />)}
              </Body>
            </Table>
          </div>
          {pagination && <Container>
            <Pagination totalPages={pagination.total_pages} totalCount={pagination.total_count} pageSize={pagination.page_size} currentPage={pagination.current_page} onPageClick={newPage => dispatch(setPage(newPage))} />
          </Container>}
          {viewDetailsRequest && <ViewDetailsPopup request={viewDetailsRequest} onClose={() => setViewDetailsRequest(null)} />}
          {cancelationRequest && <CancelationPopup request={cancelationRequest} onClose={() => setCancelationRequest(null)} />}
        </FlexLayout>
      </FlexLayout>
    </>;
};

require("./deletion_requests.wyw-in-js.css!=!../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./deletion_requests.js");